var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-source-viewer> — LOOK AT THE THING, AND AT WHAT IT IS MADE OF.
 *
 * An artifact an agent produced is two things at once: something that runs, and the text that makes
 * it run. A reader checking a widget wants both — does it draw the right thing, and what does it
 * actually read — and until now the second was a JSON string inside a collapsed disclosure and the
 * first did not exist outside a dashboard.
 *
 * THE TABS ARE NOT DRAWN HERE, and that is deliberate. They were, in a bordered bar of this
 * element's own — which put a second header-shaped band directly under the surface's real header,
 * and drew the pills UNSTYLED, because `<vitesse-pill-tabs>` is a light-DOM element that takes its
 * look from the app's global pill stylesheet and this is a shadow root that stylesheet cannot reach.
 * Both problems have one answer: the surface puts the pills in the header it already has. So this
 * exports the tab list and which one is showing, and renders only the body.
 *
 * So: one tab per document, plus an optional LIVE tab in front of them. The live tab is a slot,
 * because running a widget needs the host app's own trust boundary (its frame, its capability grant,
 * its value fan-out) and none of that belongs in a shared element. A surface that has no way to run
 * the thing simply does not pass one, and the viewer is the documents — which is exactly what the
 * fleet app and the console want from it.
 *
 * DELIBERATELY NOT AN EDITOR. It reads. Monaco, the language service and a save path are a much
 * larger decision than "show me the source", and putting a read-only editor here would make that
 * decision by accident. Copy is what a reader actually does with source they are checking.
 *
 * Generic: it knows about documents and tabs, never about widgets, agents or runs. Whoever opens it
 * names the documents.
 */
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import './vitesse-icon';
import './vitesse-pill-tabs';
import { tokenizeDoc, codeTokenCss } from '../ai/json-view';
/** The id the live tab answers to, for a host that wants to open straight onto it. */
export const LIVE_TAB = 'live';
/**
 * The bar this viewer wants, for the host to draw in its own header.
 *
 * A function rather than a property on the element so the head and the body cannot disagree about
 * what exists: both call this, and `viewerTab` below resolves the same default from the same list.
 */
export function viewerTabs(docs, hasLive, liveLabel = '') {
    return [
        ...(hasLive ? [{ id: LIVE_TAB, label: liveLabel || t('ai.viewerRender'), icon: 'monitor-play' }] : []),
        ...docs.map((d) => ({ id: d.id, label: d.label, icon: d.icon ?? 'file-text' })),
    ];
}
/** Which tab is showing: what was asked for if it still exists, else the first one. */
export function viewerTab(want, tabs) {
    return tabs.some((x) => x.id === want) ? want : (tabs[0]?.id ?? '');
}
let VitesseSourceViewer = class VitesseSourceViewer extends LitElement {
    constructor() {
        super(...arguments);
        this.docs = [];
        /** Does the host have something to RUN? When true a live tab leads the bar and shows the slot. */
        this.hasLive = false;
        /** What to call it — "Render" for a widget, "Preview" for a document. Defaults to Render. */
        this.liveLabel = '';
        /** Which tab to show. The HOST owns this — it draws the bar. Empty resolves to the first tab. */
        this.active = '';
        /**
         * The host is rendering the document itself, into the `doc` slot — its own editor, with line
         * numbers and highlighting, the way its other code surfaces look. Slotted rather than built here
         * for the same reason the live pane is: an editor belongs to the app that already ships one.
         */
        this.hostDoc = false;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
    }
    static { this.styles = [
        // The transcript's own token colours, so a payload reads the same here as it does in a tool row.
        codeTokenCss,
        css `
      :host {
        flex: 1;
        min-height: 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        background: var(--bg-primary);
      }
      .body {
        flex: 1;
        min-height: 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      /*
     * SOURCE SCROLLS IN ITS OWN BOX, in both directions.
     *
     * A column of the stack can be 300px wide and a widget's source has 120-character lines. Wrapping
     * them would renumber nothing but would make every line a paragraph, and reflowing on a drag
     * would make the whole document move while the reader resizes. So it scrolls sideways, inside
     * its own box, and the panel around it never does.
     */
      pre {
        flex: 1;
        min-height: 0;
        margin: 0;
        padding: 12px 14px;
        overflow: auto;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 12px;
        line-height: 1.55;
        color: var(--text-primary);
        tab-size: 2;
        white-space: pre;
      }
      .live {
        flex: 1;
        min-height: 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .empty {
        flex: 1;
        display: grid;
        place-items: center;
        padding: 24px;
        font-size: 12px;
        color: var(--text-secondary);
        text-align: center;
      }
    `,
    ]; }
    render() {
        const now = viewerTab(this.active, viewerTabs(this.docs, this.hasLive, this.liveLabel));
        const doc = this.docs.find((d) => d.id === now);
        return html `
      <div class="body">
        ${now === LIVE_TAB
            ? html `<div class="live"><slot name="live"></slot></div>`
            : doc
                ? this.hostDoc
                    ? html `<div class="live"><slot name="doc"></slot></div>`
                    : html `<pre>${tokenizeDoc(doc.text, doc.lang).tokens.map((tk) => html `<span class=${tk.cls}>${tk.text}</span>`)}</pre>`
                : html `<div class="empty">${t('ai.viewerEmpty')}</div>`}
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseSourceViewer.prototype, "docs", void 0);
__decorate([
    property({ type: Boolean, attribute: 'has-live', reflect: true })
], VitesseSourceViewer.prototype, "hasLive", void 0);
__decorate([
    property({ attribute: 'live-label' })
], VitesseSourceViewer.prototype, "liveLabel", void 0);
__decorate([
    property()
], VitesseSourceViewer.prototype, "active", void 0);
__decorate([
    property({ type: Boolean, attribute: 'host-doc', reflect: true })
], VitesseSourceViewer.prototype, "hostDoc", void 0);
VitesseSourceViewer = __decorate([
    customElement('vitesse-source-viewer')
], VitesseSourceViewer);
export { VitesseSourceViewer };
//# sourceMappingURL=vitesse-source-viewer.js.map